import type { FicheEditHandlers } from '@/components/templates/FicheDessinee';
import { getTemplate } from '@/components/templates/registry';
import { exportElementToPdf } from '@/lib/export/pdf';
import { exportElementToPng } from '@/lib/export/png';
import { navigate } from '@/lib/nav';
import { useEditorStore } from '@/lib/state/editorStore';
import { saveVersion } from '@/lib/storage/db';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AIPromptDrawer } from './AIPromptDrawer';
import { EditorContextProvider, type EditorMode } from './EditorContext';
import { EditorToolbar } from './EditorToolbar';
import { HistoryPanel } from './HistoryPanel';
import { SortableFicheLayer } from './SortableFicheLayer';
import { TemplatePicker } from './TemplatePicker';
import { UndoToast } from './UndoToast';
import { VersionsPanel } from './VersionsPanel';

interface EditorShellProps {
  ficheIdShort: string;
}

export function EditorShell({ ficheIdShort }: EditorShellProps) {
  const fiche = useEditorStore((s) => s.fiche);
  const pastCount = useEditorStore((s) => s.past.length);
  const futureCount = useEditorStore((s) => s.future.length);
  const setFocusedSection = useEditorStore((s) => s.setFocusedSection);

  const actions = useEditorStore(
    useShallow((s) => ({
      updateMetaTitle: s.updateMetaTitle,
      updateSectionTitle: s.updateSectionTitle,
      updateBulletText: s.updateBulletText,
      updateDrugName: s.updateDrugName,
      updateDrugSubsectionLabel: s.updateDrugSubsectionLabel,
      updateDrugSubsectionBulletText: s.updateDrugSubsectionBulletText,
      updateMnemonicText: s.updateMnemonicText,
      updateWarningText: s.updateWarningText,
      updateClinicalPatient: s.updateClinicalPatient,
      updateClinicalQuestion: s.updateClinicalQuestion,
      updateSectionWarning: s.updateSectionWarning,
      moveSection: s.moveSection,
      duplicateSection: s.duplicateSection,
      removeSection: s.removeSection,
      recolorSection: s.recolorSection,
      reorderSections: s.reorderSections,
    })),
  );

  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);

  const setTemplate = useEditorStore((s) => s.setTemplate);

  const [mode, setMode] = useState<EditorMode>('read');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);

  const loadFiche = useEditorStore((s) => s.loadFiche);

  const [toastTrigger, setToastTrigger] = useState(0);
  const [toastMessage, setToastMessage] = useState('');

  // Keyboard shortcuts: Cmd/Ctrl + Z and Cmd/Ctrl + Shift + Z
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const target = e.target as HTMLElement | null;
      const isFormish =
        target &&
        (target.tagName?.toLowerCase() === 'input' ||
          target.tagName?.toLowerCase() === 'textarea' ||
          target.isContentEditable);
      // Cmd/Ctrl + K toggles the AI drawer; safe even when typing in inputs
      // outside the drawer
      if (e.key.toLowerCase() === 'k' && !isFormish) {
        e.preventDefault();
        setAiOpen((v) => !v);
        return;
      }
      if (e.key.toLowerCase() === 'z' && !isFormish) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  // Click outside any card clears focus. We use a ref on the main sheet to
  // detect clicks outside cards/toolbar/drawer.
  const sheetRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Anywhere inside a card → don't clear
      if (target.closest('[data-section-id]')) return;
      // Inside the AI drawer or toolbar → don't clear
      if (target.closest('[data-testid="ai-drawer"]')) return;
      if (target.closest('[data-testid="editor-root"] > div:first-child')) return;
      if (target.closest('[data-testid="history-panel"]')) return;
      setFocusedSection(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [setFocusedSection]);

  const handleExport = async (kind: 'pdf' | 'png') => {
    if (!fiche) return;
    const sheet = document.querySelector('[data-testid="editor-root"]') as HTMLElement | null;
    if (!sheet) return;
    const safeTitle = fiche.meta.title
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-zA-Z0-9-]+/g, '_')
      .slice(0, 60);
    const filename = `${safeTitle || 'fiche'}.${kind}`;
    try {
      setToastMessage(`Export ${kind.toUpperCase()} en cours…`);
      setToastTrigger((t) => t + 1);
      if (kind === 'pdf') {
        await exportElementToPdf(sheet, { filename });
      } else {
        await exportElementToPng(sheet, { filename });
      }
      setToastMessage(`✅ ${filename} téléchargé`);
      setToastTrigger((t) => t + 1);
    } catch (err) {
      console.error('Export failed:', err);
      setToastMessage(`Échec export ${kind.toUpperCase()}`);
      setToastTrigger((t) => t + 1);
    }
  };

  const handleSaveVersion = async () => {
    if (!fiche) return;
    const name = window.prompt(
      'Nom de cette version :',
      `v-${new Date().toISOString().slice(0, 16)}`,
    );
    if (!name) return;
    try {
      await saveVersion(fiche.id, name.trim(), fiche);
      setToastMessage(`✅ Version « ${name.trim()} » sauvegardée`);
      setToastTrigger((t) => t + 1);
    } catch (err) {
      console.error('Save version failed:', err);
      setToastMessage('Échec sauvegarde version');
      setToastTrigger((t) => t + 1);
    }
  };

  if (!fiche) return null;

  const editHandlers: FicheEditHandlers = mode === 'edit' ? actions : {};
  const template = getTemplate(fiche.meta.template_id);
  const TemplateComponent = template.Component;

  return (
    <EditorContextProvider mode={mode}>
      <main
        ref={sheetRef}
        data-testid="editor-root"
        className={historyOpen ? 'pr-[320px]' : ''}
        style={{ transition: 'padding-right 200ms ease' }}
      >
        <EditorToolbar
          mode={mode}
          onToggleMode={() => setMode((m) => (m === 'edit' ? 'read' : 'edit'))}
          onUndo={undo}
          onRedo={redo}
          canUndo={pastCount > 0}
          canRedo={futureCount > 0}
          onToggleHistory={() => setHistoryOpen((v) => !v)}
          historyOpen={historyOpen}
          onToggleAi={() => setAiOpen((v) => !v)}
          aiOpen={aiOpen}
          onToggleTemplatePicker={() => setTemplatePickerOpen((v) => !v)}
          templatePickerOpen={templatePickerOpen}
          currentTemplateLabel={template.label}
          onExportPdf={() => handleExport('pdf')}
          onExportPng={() => handleExport('png')}
          onSaveVersion={handleSaveVersion}
          onShowVersions={() => setVersionsOpen(true)}
          onShowLibrary={() => navigate('/library')}
          ficheIdShort={ficheIdShort}
        />
        {mode === 'edit' && fiche.meta.template_id === 'dessinee' ? (
          <SortableFicheLayer fiche={fiche} editHandlers={editHandlers} />
        ) : (
          <TemplateComponent fiche={fiche} editHandlers={editHandlers} />
        )}
      </main>
      {historyOpen ? (
        <HistoryPanel
          pastCount={pastCount}
          futureCount={futureCount}
          onJumpBack={(steps) => {
            for (let i = 0; i < steps; i++) undo();
          }}
          onJumpForward={(steps) => {
            for (let i = 0; i < steps; i++) redo();
          }}
          onClose={() => setHistoryOpen(false)}
        />
      ) : null}
      <AIPromptDrawer
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        fiche={fiche}
        onPatchApplied={({ count, modelUsed, latencyMs }) => {
          setToastMessage(
            `IA : ${count} modification${count > 1 ? 's' : ''} appliquée${count > 1 ? 's' : ''} (${modelUsed} · ${latencyMs} ms)`,
          );
          setToastTrigger((t) => t + 1);
          setFocusedSection(null);
        }}
      />
      <UndoToast trigger={toastTrigger} message={toastMessage} onUndo={undo} />
      {templatePickerOpen ? (
        <TemplatePicker
          fiche={fiche}
          currentId={fiche.meta.template_id}
          onPick={(id) => {
            setTemplate(id);
            setTemplatePickerOpen(false);
          }}
          onClose={() => setTemplatePickerOpen(false)}
        />
      ) : null}
      {versionsOpen ? (
        <VersionsPanel
          ficheId={fiche.id}
          currentFiche={fiche}
          onRestore={(restored) => {
            loadFiche(restored);
            setVersionsOpen(false);
            setToastMessage('✅ Version restaurée');
            setToastTrigger((t) => t + 1);
          }}
          onClose={() => setVersionsOpen(false)}
        />
      ) : null}
    </EditorContextProvider>
  );
}
